<template>
  <div
    v-bind="attrs"
    :id="rootId"
    ref="rootRef"
    :class="DS_PAGE_COLUMNS_CLASSES.Root"
    :style="gridStyle"
    data-cy="ds-page-columns"
    @[DS_RESIZE_HANDLE_NATIVE_EVENT.ResizeStart]="onResizeHandleNativeStart"
  >
    <template
      v-for="(slotName, index) in columnSlotNames"
      :key="slotName"
    >
      <div :class="DS_PAGE_COLUMNS_CLASSES.Column">
        <slot :name="slotName" />
      </div>
      <DSResizeHandle
        v-if="resizeHandles[index]"
        :key="`${columnSlotSignature}:${slotName}`"
        :ref="element => setResizeHandleRef(index, element as InstanceType<typeof DSResizeHandle> | null)"
        v-bind="resizeHandleProps"
        :class="DS_PAGE_COLUMNS_CLASSES.ResizeHandle"
        :style="resizeHandles[index].style"
        :model-value="resizeHandles[index].modelValue"
        :min="resizeHandles[index].min"
        :max="resizeHandles[index].max"
        :step="resizeHandles[index].step"
        :aria-value-text="resizeHandles[index].ariaValueText"
        :disabled="isResizeHandleDisabled(index)"
        @resize-start="onResizeStart(index, $event)"
        @update:model-value="onUpdateModelValue(index, $event)"
        @resize-end="onResizeEnd(index, $event)"
      />
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUpdate, onMounted, ref, useAttrs, useSlots } from "vue";
import DSResizeHandle from "@/src/design_system/primitives/resize_handle/components/DSResizeHandle.vue";
import { DS_RESIZE_HANDLE_NATIVE_EVENT } from "@/src/design_system/primitives/resize_handle/types/emits";
import { DS_CUSTOM_ELEMENT_NAME, customElementTag } from "@/src/design_system/shared/constants/customElementNames";
import { generateUUID } from "@/src/shared/utils/uuid";
import { useDSPageColumns } from "../composables/useDSPageColumns";
import { DS_PAGE_COLUMNS_CLASSES } from "../constants/classes";
import type { DSPageColumnsEmits } from "../types/emits";
import type { DSPageColumnsProps } from "../types/props";
import { getPageColumnSlotNames } from "../utils/pageColumns";

const props = withDefaults(defineProps<DSPageColumnsProps>(), {
  minColumnWidth: 0,
});
const emit = defineEmits<DSPageColumnsEmits>();
defineOptions({ inheritAttrs: false });

const attrs = useAttrs();
const slots = useSlots();
const rootRef = ref<HTMLElement>();
const hasCustomElementHost = ref(false);
const columnSlotNames = ref(getPageColumnSlotNames(Object.keys(slots)));
const columnSlotSignature = computed(() => columnSlotNames.value.join("|"));
const resizeHandleElements: Array<HTMLElement | undefined> = [];
const generatedRootId = `ds-page-columns-${generateUUID()}`;
const rootId = computed(() => (!hasCustomElementHost.value && attrs.id ? String(attrs.id) : generatedRootId));
const setResizeHandleRef = (index: number, handle: InstanceType<typeof DSResizeHandle> | null) => {
  resizeHandleElements[index] = handle?.$el;
};
const transferResizeHandleFocus = (sourceIndex: number, ownerIndex: number) => {
  if (document.activeElement !== resizeHandleElements[sourceIndex]) return;

  void nextTick(() => resizeHandleElements[ownerIndex]?.focus());
};
const {
  cancelPointerResize,
  gridStyle,
  isResizeHandleDisabled,
  onResizeHandleNativeStart,
  resizeHandleProps,
  resizeHandles,
  onResizeEnd,
  onResizeStart,
  onUpdateModelValue,
} = useDSPageColumns({
  props,
  columnCount: computed(() => columnSlotNames.value.length),
  rootId,
  rootRef,
  onFocusTransfer: transferResizeHandleFocus,
  onUpdate: value => emit("update:modelValue", value),
});

onBeforeUpdate(() => {
  const nextSlotNames = getPageColumnSlotNames(Object.keys(slots));
  if (nextSlotNames.join("|") === columnSlotSignature.value) return;

  cancelPointerResize();
  columnSlotNames.value = nextSlotNames;
});

onMounted(() => {
  hasCustomElementHost.value =
    rootRef.value?.parentElement?.localName === customElementTag(DS_CUSTOM_ELEMENT_NAME.PageColumns);
});
</script>
